/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import {
    ORGANIZATION_DELETE_START,
    ORGANIZATION_DELETE_SUCCESS,
    ORGANIZATION_ERROR,
    ORGANIZATION_CLEAR_ERROR,
    ORGANIZATION_CLEAR_SUCCESS,
    ORGANIZATION_FETCH_START,
    ORGANIZATION_FETCH_SUCCESS,
    ORGANIZATION_RESET,
    ORGANIZATION_SAVE_START,
    ORGANIZATION_SAVE_SUCCESS,
    OrganizationAction
} from './OrganizationActionsTypes';
import { convertToSaveOrganizationDto, convertToOrganization } from './OrganizationConverter';
import { Organization, SaveOrganization } from 'model/organization';
import { FetchOrganizationsDto } from './OrganizationDtos';

const organizationsUrl = '/organizations';

// Extract meaningful error message from API error response
const extractErrorMessage = (apiError: any): string => {
    // Handle Axios error structure
    if (apiError?.response?.data) {
        const { data } = apiError.response;

        // Check for validation errors (422 status) - direct field errors like {"name":["has already been taken"]}
        if (typeof data === 'object' && !data.message && !data.error && !data.errors) {
            // Look for field validation errors directly on data object
            const fieldNames = Object.keys(data);
            if (fieldNames.length > 0) {
                const firstField = fieldNames[0];
                const fieldError = data[firstField];
                if (Array.isArray(fieldError) && fieldError.length > 0) {
                    return `${firstField} ${fieldError[0]}`;
                }
                if (typeof fieldError === 'string') {
                    return `${firstField} ${fieldError}`;
                }
            }
        }

        // Check for validation errors in 'errors' property
        if (data.errors) {
            if (typeof data.errors === 'object') {
                // Extract first error from validation errors object
                const firstField = Object.keys(data.errors)[0];
                const firstError = data.errors[firstField];
                if (Array.isArray(firstError)) {
                    return firstError[0];
                }
                return firstError;
            }
            return data.errors;
        }

        // Check for general error message
        if (data.message) {
            return data.message;
        }

        // Check for error field
        if (data.error) {
            return data.error;
        }
    }

    // Fallback to Axios message or generic error
    if (apiError?.message) {
        return apiError.message;
    }

    // Final fallback
    return 'An unexpected error occurred. Please try again.';
};

const error = (err: string): OrganizationAction => {
    return {
        type: ORGANIZATION_ERROR,
        payload: {
            error: err
        }
    };
};

export const clearOrganizationError = (): OrganizationAction => {
    return {
        type: ORGANIZATION_CLEAR_ERROR
    };
};

export const clearOrganizationSuccess = (): OrganizationAction => {
    return {
        type: ORGANIZATION_CLEAR_SUCCESS
    };
};

export const resetOrganizationState = (): OrganizationAction => {
    return {
        type: ORGANIZATION_RESET
    };
};

export const fetchOrganizationsThunk = (): ThunkAction<
    Promise<Array<Organization>>,
    RootState,
    null,
    OrganizationAction
> => {
    return async dispatch => {
        dispatch({ type: ORGANIZATION_FETCH_START });

        try {
            const { data }: AxiosResponse<FetchOrganizationsDto> = await ApiClient.get(organizationsUrl, {
                /* params: {
                    page,
                    size,
                    name,
                    status,
                }, */
            });

            // Convert DTOs to Organization models
            const organizationDtos = data.organizations || [];
            const organizations = organizationDtos.map(convertToOrganization);

            dispatch({
                type: ORGANIZATION_FETCH_SUCCESS,
                payload: {
                    organizations,
                    pagination: data.pagination
                        ? {
                              currentPage: data.pagination.current_page,
                              nextPage: data.pagination.next_page,
                              prevPage: data.pagination.prev_page,
                              size: data.pagination.per_page,
                              totalPages: data.pagination.total_pages,
                              totalRecords: data.pagination.total_count
                          }
                        : undefined
                }
            });

            return organizations;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

export const saveOrganizationThunk = (
    organization: SaveOrganization
): ThunkAction<Promise<Organization>, RootState, null, OrganizationAction> => {
    return async dispatch => {
        dispatch({ type: ORGANIZATION_SAVE_START });
        const url = organization.id ? `${organizationsUrl}/${organization.id}` : organizationsUrl;
        const apiMethod = organization.id ? ApiClient.patch : ApiClient.post;

        try {
            // Convert form data or SaveOrganization model directly to DTO (API payload format)
            const payload = convertToSaveOrganizationDto(organization);

            const { data }: AxiosResponse<any> = await apiMethod(url, payload);

            // Convert the DTO response back to Organization model format
            const savedOrganization = convertToOrganization(data);

            dispatch({
                type: ORGANIZATION_SAVE_SUCCESS,
                payload: {
                    organization: savedOrganization
                }
            });

            return savedOrganization;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

export const deleteOrganizationThunk = (
    organizationId: number
): ThunkAction<Promise<void>, RootState, null, OrganizationAction> => {
    return async dispatch => {
        dispatch({ type: ORGANIZATION_DELETE_START });

        const url = `${organizationsUrl}/${organizationId}`;
        const apiMethod = ApiClient.delete;

        try {
            await apiMethod(url);

            dispatch({
                type: ORGANIZATION_DELETE_SUCCESS,
                payload: {
                    organizationId
                }
            });
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};
